import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Users, Plus, Search, RefreshCw, Mail, Phone, Building2, Trash2, Edit, Key, Copy, Eye, EyeOff
} from 'lucide-react';

const ROLES = [
  { value: 'department_admin', label: 'Department Admin' },
  { value: 'field_investigator', label: 'Field Investigator' },
  { value: 'skip_tracer', label: 'Skip Tracer' },
  { value: 'transporter', label: 'Transporter' },
  { value: 'client', label: 'Client' },
];

const ROLE_COLORS = {
  department_admin: 'bg-amber-500', field_investigator: 'bg-blue-500',
  skip_tracer: 'bg-purple-500', transporter: 'bg-cyan-500', client: 'bg-green-500'
};

const MainHubUsers = () => {
  const token = localStorage.getItem('nexus_main_hub_token');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [form, setForm] = useState({
    username: '', password: '', email: '', name: '', phone: '',
    role: 'field_investigator', company_id: '', title: '', department: '', badge_number: ''
  });
  const [editForm, setEditForm] = useState({
    name: '', email: '', phone: '', role: '', newPassword: '', title: '', department: '', badge_number: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, companiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/main-hub/users`, { headers }),
        fetch(`${BACKEND_URL}/api/main-hub/companies`, { headers })
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (companiesRes.ok) setCompanies(await companiesRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generatePassword = async () => {
    setGeneratingPassword(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/generate-password`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        return data.password;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingPassword(false);
    }
    return null;
  };

  const handleGeneratePasswordForCreate = async () => {
    const password = await generatePassword();
    if (password) {
      setForm({ ...form, password });
      toast({ title: 'Password Generated', description: 'Secure password has been generated. Make sure to copy it!' });
    }
  };

  const handleGeneratePasswordForEdit = async () => {
    const password = await generatePassword();
    if (password) {
      setEditForm({ ...editForm, newPassword: password });
      toast({ title: 'Password Generated', description: 'Secure password has been generated. Make sure to copy it!' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: 'Password copied to clipboard' });
  };

  const handleCreate = async () => {
    if (!form.username || !form.password || !form.email || !form.name || !form.company_id) {
      toast({ title: 'Error', description: 'All required fields must be filled', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'User created' });
        setShowCreate(false);
        setForm({ username: '', password: '', email: '', name: '', phone: '', role: 'field_investigator', company_id: '', title: '', department: '', badge_number: '' });
        fetchData();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleEdit = async () => {
    if (!editForm.name || !editForm.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    try {
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        role: editForm.role,
        title: editForm.title,
        department: editForm.department,
        badge_number: editForm.badge_number
      };
      
      // Only include password if a new one was set
      if (editForm.newPassword) {
        updateData.password = editForm.newPassword;
      }

      const res = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updateData)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'User updated' });
        setShowEdit(false);
        setSelectedUser(null);
        setEditForm({ name: '', email: '', phone: '', role: '', newPassword: '', title: '', department: '', badge_number: '' });
        fetchData();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'User deleted' });
        fetchData();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast({ title: 'Success', description: `User ${newStatus.toLowerCase()}` });
        fetchData();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'field_investigator',
      newPassword: '',
      title: user.title || '',
      department: user.department || '',
      badge_number: user.badge_number || ''
    });
    setShowEdit(true);
  };

  const getCompanyName = (companyId) => companies.find(c => c.id === companyId)?.name || companyId || 'N/A';

  const filtered = users.filter(u => {
    if (roleFilter !== 'ALL' && u.role !== roleFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="main-hub-users">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-400">Manage all platform users across companies</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchData}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
              <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="John Doe" data-testid="user-name-input" />
                  </div>
                  <div>
                    <Label>Username *</Label>
                    <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="johndoe" data-testid="user-username-input" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="john@company.com" data-testid="user-email-input" />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          value={form.password} 
                          onChange={e => setForm({ ...form, password: e.target.value })}
                          className="bg-slate-50 border-slate-300 text-slate-900 pr-16" 
                          placeholder="Password" 
                          data-testid="user-password-input" 
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="border-slate-300 text-amber-400 hover:bg-amber-500/10 px-2"
                        onClick={handleGeneratePasswordForCreate}
                        disabled={generatingPassword}
                        data-testid="generate-password-btn"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {form.password && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="border-slate-300 text-slate-400 hover:text-white px-2"
                          onClick={() => copyToClipboard(form.password)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="(555) 000-0000" />
                  </div>
                  <div>
                    <Label>Role *</Label>
                    <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Company *</Label>
                    <Select value={form.company_id} onValueChange={v => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="user-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title / Position</Label>
                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Senior Investigator" data-testid="user-title-input" />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Investigations" data-testid="user-department-input" />
                  </div>
                  <div className="col-span-2">
                    <Label>Badge / Certification Number</Label>
                    <Input value={form.badge_number} onChange={e => setForm({ ...form, badge_number: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900" placeholder="PI-2025-001" data-testid="user-badge-input" />
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="submit-user-btn">
                  Create User
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search users..." data-testid="user-search" />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Roles</SelectItem>
                {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-right text-sm text-slate-400 flex items-center justify-end">
              {filtered.length} of {users.length} users
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading users...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Users Found</h3>
            <p className="text-slate-400">{search || roleFilter !== 'ALL' ? 'Try different filters' : 'Add your first user'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(u => (
            <Card key={u.id} className="bg-white border-slate-200" data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{u.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{u.name}</span>
                        <Badge className={`${ROLE_COLORS[u.role] || 'bg-slate-500'} text-white text-xs`}>
                          {u.role?.replace(/_/g, ' ')}
                        </Badge>
                        <Badge className={`${u.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} text-xs`}>
                          {u.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mt-1">
                        <span>@{u.username}</span>
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
                        {u.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span>}
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{getCompanyName(u.company_id)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline"
                      className={`text-xs ${u.status === 'ACTIVE' ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-green-500/50 text-green-400 hover:bg-green-500/10'}`}
                      onClick={() => toggleStatus(u.id, u.status)}
                      data-testid={`toggle-status-${u.id}`}>
                      {u.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    </Button>
                    <button 
                      onClick={() => openEditDialog(u)} 
                      className="text-slate-400 hover:text-amber-400 p-1.5"
                      data-testid={`edit-user-${u.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(u.id)} 
                      className="text-slate-500 hover:text-red-400 p-1.5" 
                      data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) setSelectedUser(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader><DialogTitle>Edit User: {selectedUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="John Doe" data-testid="edit-user-name-input" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="john@company.com" data-testid="edit-user-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="(555) 000-0000" />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Reset Password (leave empty to keep current)</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input 
                      type={showPassword ? "text" : "password"} 
                      value={editForm.newPassword} 
                      onChange={e => setEditForm({ ...editForm, newPassword: e.target.value })}
                      className="bg-slate-50 border-slate-300 text-slate-900 pr-10" 
                      placeholder="New password (optional)" 
                      data-testid="edit-user-password-input" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="border-slate-300 text-amber-400 hover:bg-amber-500/10 px-3"
                    onClick={handleGeneratePasswordForEdit}
                    disabled={generatingPassword}
                    data-testid="edit-generate-password-btn"
                  >
                    <Key className="w-4 h-4 mr-2" /> Generate
                  </Button>
                  {editForm.newPassword && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="border-slate-300 text-slate-400 hover:text-white px-2"
                      onClick={() => copyToClipboard(editForm.newPassword)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label>Title / Position</Label>
                <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Senior Investigator" data-testid="edit-user-title-input" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={editForm.department} onChange={e => setEditForm({ ...editForm, department: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="Investigations" data-testid="edit-user-department-input" />
              </div>
              <div className="col-span-2">
                <Label>Badge / Certification Number</Label>
                <Input value={editForm.badge_number} onChange={e => setEditForm({ ...editForm, badge_number: e.target.value })}
                  className="bg-slate-50 border-slate-300 text-slate-900" placeholder="PI-2025-001" data-testid="edit-user-badge-input" />
              </div>
            </div>
            <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
              <p className="text-xs text-slate-400">
                <strong>Username:</strong> {selectedUser?.username} (cannot be changed)<br />
                <strong>Company:</strong> {getCompanyName(selectedUser?.company_id)} (cannot be changed)
              </p>
            </div>
            <Button onClick={handleEdit} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-user-btn">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainHubUsers;
